import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface ZoomAccount {
  id: string;
  zoom_email: string;
  account_type: string;
}

export default function CreateMeetingPage() {
  const navigate = useNavigate();
  const [zoomAccounts, setZoomAccounts] = useState<ZoomAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    zoom_account_id: '',
    topic: '',
    description: '',
    start_time: '',
    duration: 60,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    is_instant: false,
  });

  useEffect(() => {
    loadZoomAccounts();
  }, []);

  const loadZoomAccounts = async () => {
    try {
      const response = await api.getZoomAccounts();
      setZoomAccounts(response.accounts || []);
      if (response.accounts?.length > 0) {
        setFormData(prev => ({ ...prev, zoom_account_id: response.accounts[0].id }));
      }
    } catch (err) {
      console.error('Failed to load Zoom accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const startTime = formData.is_instant
        ? new Date().toISOString()
        : new Date(formData.start_time).toISOString();

      const response = await api.createMeeting({
        zoom_account_id: formData.zoom_account_id,
        topic: formData.topic,
        description: formData.description,
        start_time: startTime,
        duration: formData.duration,
        timezone: formData.timezone,
        meeting_type: formData.is_instant ? 'instant' : 'scheduled',
      });

      // Navigate to the meeting page or create embed
      navigate(`/meetings?created=${response.meeting.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create meeting');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (zoomAccounts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/dashboard" className="text-2xl font-bold text-indigo-600">EmbedZoom</Link>
          </div>
        </nav>
        <main className="max-w-2xl mx-auto px-6 py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">No Zoom Account Connected</h1>
          <p className="text-gray-600 mb-6">You need to connect a Zoom account before creating meetings.</p>
          <Link to="/dashboard" className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Go to Dashboard
          </Link>
        </main>
      </div>
    );
  }

  // Get min date-time (now)
  const minDateTime = new Date().toISOString().slice(0, 16);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="text-2xl font-bold text-indigo-600">EmbedZoom</Link>
          <Link to="/dashboard" className="text-gray-600 hover:text-gray-800">
            Back to Dashboard
          </Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Create New Meeting</h1>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* Zoom Account Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zoom Account
            </label>
            <select
              value={formData.zoom_account_id}
              onChange={(e) => setFormData({ ...formData, zoom_account_id: e.target.value })}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {zoomAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.zoom_email} ({account.account_type})
                </option>
              ))}
            </select>
          </div>

          {/* Meeting Topic */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Topic *
            </label>
            <input
              type="text"
              value={formData.topic}
              onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
              required
              maxLength={200}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="e.g., Weekly Team Standup"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              maxLength={2000}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Optional meeting description..."
            />
          </div>

          {/* Meeting Time Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              When to Start
            </label>
            <div className="flex items-center space-x-4 mb-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="meeting_time"
                  checked={formData.is_instant}
                  onChange={() => setFormData({ ...formData, is_instant: true, start_time: '' })}
                  className="w-4 h-4 text-indigo-600"
                />
                <span className="ml-2 text-sm text-gray-700">Start Now (Instant Meeting)</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="meeting_time"
                  checked={!formData.is_instant}
                  onChange={() => setFormData({ ...formData, is_instant: false })}
                  className="w-4 h-4 text-indigo-600"
                />
                <span className="ml-2 text-sm text-gray-700">Schedule for Later</span>
              </label>
            </div>

            {!formData.is_instant && (
              <>
                <input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required={!formData.is_instant}
                  min={minDateTime}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Timezone: {formData.timezone}
                </p>
              </>
            )}

            {formData.is_instant && (
              <p className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                The meeting will start immediately and you can join right away.
              </p>
            )}
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration (minutes) *
            </label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-4">
            <Link
              to="/dashboard"
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Meeting'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
